<template>
    <div class="series-chart" v-bind:style="{ height: `${height}px` }">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';

const pad = (value) => String(value).padStart(2, '0');

/**
 * The server returns utc as 'YYYY-MM-DD HH:00:00'. Read it as utc and show it in
 * the reader's own time, which is what every other timestamp on these screens
 * does - see WeatherCard.
 */
const localLabel = (stamp) =>
{
    const parsed = new Date(`${String(stamp).replace(' ', 'T')}Z`);

    if (Number.isNaN(parsed.getTime()))
    {
        return String(stamp);
    }

    return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const PALETTE = [
    '#20a8d8', '#f86c6b', '#4dbd74', '#ffc107', '#6f42c1', '#fd7e14',
    '#20c997', '#e83e8c', '#6610f2', '#795548', '#607d8b', '#00bcd4',
];

export default
{
    name: 'SeriesChart',
    props:
    {
        // single series: [{ dateTime, value }], oldest first, one point per hour
        points:
        {
            type: Array,
            default: () => [],
        },
        // several series at once: [{ key, label, points, hidden }]
        series:
        {
            type: Array,
            default: null,
        },
        label:
        {
            type: String,
            default: '',
        },
        height:
        {
            type: [Number, String],
            default: 200,
        },
    },
    mounted()
    {
        this.draw();
    },
    beforeDestroy()
    {
        if (this.chart != null)
        {
            this.chart.destroy();
            this.chart = null;
        }
    },
    watch:
    {
        points()
        {
            this.draw();
        },
        series()
        {
            this.draw();
        },
    },
    methods:
    {
        draw()
        {
            const me = this;

            if (me.$refs.canvas == null)
            {
                return;
            }

            const list = (me.series != null)
                ? me.series
                : [{ key: 'single', label: me.label, points: me.points }];

            // Series do not have to share timestamps - module_log and module_values
            // are written by different paths - so the x axis is the union of every
            // hour any of them reported, and a series that missed one gets a null.
            const stamps = [];

            for (const one of list)
            {
                for (const point of (one.points || []))
                {
                    if (stamps.indexOf(point.dateTime) === -1)
                    {
                        stamps.push(point.dateTime);
                    }
                }
            }

            stamps.sort();

            const datasets = list.map((one, index) =>
            {
                const byStamp = {};

                for (const point of (one.points || []))
                {
                    byStamp[point.dateTime] = (point.value == null) ? null : Number(point.value);
                }

                const color = PALETTE[index % PALETTE.length];

                return {
                    label: one.label,
                    backgroundColor: 'transparent',
                    borderColor: color,
                    pointBackgroundColor: color,
                    borderWidth: 1.5,
                    pointRadius: 0,
                    pointHitRadius: 8,
                    lineTension: 0.2,
                    spanGaps: true,
                    // the same flag the legend writes when someone clicks an entry,
                    // so the starting state and a click mean exactly one thing
                    hidden: (one.hidden === true),
                    data: stamps.map((stamp) => ((byStamp[stamp] === undefined) ? null : byStamp[stamp])),
                };
            });

            if (me.chart != null)
            {
                me.chart.destroy();
            }

            me.chart = new Chart(me.$refs.canvas.getContext('2d'),
                {
                    type: 'line',
                    data: { labels: stamps.map(localLabel), datasets },
                    options:
                    {
                        responsive: true,
                        maintainAspectRatio: false,
                        legend:
                        {
                            display: (me.series != null),
                            position: 'bottom',
                            labels: { usePointStyle: true, boxWidth: 8 },
                        },
                        tooltips: { intersect: false, mode: 'index' },
                        scales:
                        {
                            // a week of hourly points is far too many to label, and
                            // the y axis has to follow the data - pressure sits near
                            // 1000, a dew point near 18, neither starts at zero
                            xAxes: [{ ticks: { maxTicksLimit: 7, maxRotation: 0, autoSkip: true } }],
                            yAxes: [{ ticks: { beginAtZero: false } }],
                        },
                    },
                });
        },
    },
};
</script>

<style scoped>
/* chart.js measures the canvas against this box, so it needs a real height */
.series-chart { position: relative; width: 100%; }
</style>
